import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Briefcase, Sparkles, Copy, Check, Trash2, 
  ExternalLink, ChevronDown, ChevronUp, RefreshCw, Plus,
  Target, Send, Award, X, FileText, Zap, ArrowLeft,
  Mail, Sun, Moon, Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { upworkAPI, apiErrorMessage } from '../lib/api';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import Logo from '../components/Logo';

const STATUS_COLORS = {
  new: 'bg-blue-500/20 text-blue-400',
  proposal_ready: 'bg-cyan-500/20 text-cyan-400',
  applied: 'bg-yellow-500/20 text-yellow-400',
  won: 'bg-green-500/20 text-green-400',
  lost: 'bg-red-500/20 text-red-400',
  skipped: 'bg-gray-500/20 text-gray-400',
};

const STATUS_LABELS = {
  new: 'New',
  proposal_ready: 'Proposal Ready',
  applied: 'Applied',
  won: 'Won',
  lost: 'Lost',
  skipped: 'Skipped',
};

export default function UpworkScoutPage() {
  const { isDark, toggleTheme } = useTheme();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedJob, setExpandedJob] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  
  // Import form state
  const [importForm, setImportForm] = useState({
    title: '', url: '', description: '', budget: '', 
    duration: '', skills: '', experience_level: ''
  });
  const [bulkMarkdown, setBulkMarkdown] = useState('');
  const [bulkQuery, setBulkQuery] = useState('');
  const [emailHtml, setEmailHtml] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [jobsRes, statsRes] = await Promise.all([
        upworkAPI.getJobs(filterStatus ? { status: filterStatus } : {}),
        upworkAPI.getStats()
      ]);
      setJobs(jobsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleImportJob = async () => {
    if (!importForm.title || !importForm.description) {
      toast.error('Title and description are required');
      return;
    }
    try {
      await upworkAPI.importJob({
        ...importForm,
        skills: importForm.skills.split(',').map(s => s.trim()).filter(Boolean)
      });
      toast.success('Job imported!');
      setShowImportModal(false);
      setImportForm({ title: '', url: '', description: '', budget: '', duration: '', skills: '', experience_level: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to import job');
    }
  };

  const handleBulkImport = async () => {
    if (!bulkMarkdown) {
      toast.error('Paste the Upwork search results content');
      return;
    }
    try {
      const res = await upworkAPI.importBulk({ markdown: bulkMarkdown, search_query: bulkQuery || 'bulk_import' });
      toast.success(`Imported ${res.data.total_imported} jobs (${res.data.total_skipped} duplicates skipped)`);
      setShowBulkModal(false);
      setBulkMarkdown('');
      loadData();
    } catch (error) {
      toast.error('Failed to import');
    }
  };

  const handleGenerateProposal = async (jobId) => {
    setGeneratingId(jobId);
    try {
      const res = await upworkAPI.generateProposal(jobId);
      toast.success('Proposal generated!');
      setExpandedJob(jobId);
      loadData();
    } catch (error) {
      toast.error('Failed to generate proposal');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleCopyProposal = (proposal, jobId) => {
    navigator.clipboard.writeText(proposal);
    setCopiedId(jobId);
    toast.success('Proposal copied to clipboard!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleUpdateStatus = async (jobId, status) => {
    try {
      await upworkAPI.updateStatus(jobId, status);
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await upworkAPI.deleteJob(jobId);
      toast.success('Job deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleGenerateAllProposals = async () => {
    try {
      toast.info('Generating proposals for all new jobs... This may take a minute.');
      const res = await upworkAPI.generateAllProposals();
      toast.success(`Generated ${res.data.proposals_generated} proposals`);
      loadData();
    } catch (error) {
      toast.error('Batch generation failed');
    }
  };

  const handleEmailImport = async () => {
    if (!emailHtml.trim()) {
      toast.error('Paste the email content (HTML or text)');
      return;
    }
    try {
      const res = await upworkAPI.importEmail(emailHtml);
      const { imported, rejected, duplicates, errors } = res.data;
      if (imported.length > 0) {
        toast.success(`Imported: ${imported[0].title}`);
      } else if (rejected.length > 0) {
        toast.warning(`Rejected: ${rejected[0].reason}`);
      } else if (duplicates.length > 0) {
        toast.info('Job already exists');
      } else if (errors.length > 0) {
        toast.error(errors[0]);
      }
      setShowEmailModal(false);
      setEmailHtml('');
      loadData();
    } catch (error) {
      toast.error('Failed to import from email');
    }
  };

  const handleScanEmails = async () => {
    try {
      toast.info('Scanning emails for Upwork jobs...');
      const res = await upworkAPI.scanEmails();
      toast.success(res.data.message || 'Email scan complete');
      loadData();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Email scan failed - Graph API may not be configured'));
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#050510] text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`border-b ${isDark ? 'border-white/10 bg-[#0A0A12]' : 'border-gray-200 bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 hover:opacity-80">
              <ArrowLeft className="w-4 h-4" />
              <Logo size={28} isDark={isDark} />
              <span className="font-heading font-bold">XceedAgents</span>
            </Link>
            <span className={isDark ? 'text-white/30' : 'text-gray-300'}>|</span>
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-[#00F0FF]" />
              <h1 className="font-heading text-lg font-bold">Upwork Scout</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              data-testid="upwork-theme-toggle"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <Button 
              size="sm" variant="outline" className={`text-sm gap-1 ${isDark ? 'border-white/20' : 'border-gray-300'}`}
              onClick={() => setShowEmailModal(true)}
              data-testid="email-import-btn"
            >
              <Mail className="w-3.5 h-3.5" /> Email Import
            </Button>
            <Button 
              size="sm" variant="outline" className={`text-sm gap-1 ${isDark ? 'border-white/20' : 'border-gray-300'}`}
              onClick={() => setShowBulkModal(true)}
              data-testid="bulk-import-btn"
            >
              <FileText className="w-3.5 h-3.5" /> Bulk Import
            </Button>
            <Button 
              size="sm" className="bg-[#00F0FF] text-black text-sm gap-1"
              onClick={() => setShowImportModal(true)}
              data-testid="import-job-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Import Job
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { label: 'Total Jobs', value: stats.total || 0, color: isDark ? '#fff' : '#111' },
            { label: 'New', value: stats.new || 0, color: '#3B82F6' },
            { label: 'Proposals Ready', value: stats.proposal_ready || 0, color: '#00F0FF' },
            { label: 'Applied', value: stats.applied || 0, color: '#FFD600' },
            { label: 'Won', value: stats.won || 0, color: '#00FF94' },
          ].map(s => (
            <Card key={s.label} className={`p-4 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`} data-testid={`stat-${s.label.toLowerCase().replace(' ','-')}`}>
              <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
              <div className={`text-xs uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{s.label}</div>
            </Card>
          ))}
        </div>

        {/* Filters + Actions */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex gap-2">
            {['', 'new', 'proposal_ready', 'applied', 'won', 'lost', 'skipped'].map(s => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 text-xs transition-colors ${
                  filterStatus === s 
                    ? 'bg-[#00F0FF]/20 text-[#00F0FF] border border-[#00F0FF]/40'
                    : isDark ? 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10' : 'bg-gray-100 text-gray-500 border border-gray-200 hover:bg-gray-200'
                }`}
                data-testid={`filter-${s || 'all'}`}
              >
                {s ? STATUS_LABELS[s] : 'All'}
              </button>
            ))}
          </div>
          <div className="ml-auto">
            <Button
              size="sm"
              variant="outline"
              className="border-[#7000FF]/40 text-[#7000FF] text-xs gap-1"
              onClick={handleGenerateAllProposals}
              data-testid="generate-all-btn"
            >
              <Sparkles className="w-3.5 h-3.5" /> Generate All Proposals
            </Button>
          </div>
        </div>

        {/* Job Listings */}
        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading jobs...</div>
        ) : jobs.length === 0 ? (
          <Card className={`p-12 text-center ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <Briefcase className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">No Jobs Scouted Yet</h3>
            <p className={`mb-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Import Upwork jobs to get started. Paste a job URL, use bulk import, or import from email.</p>
            <div className="flex gap-3 justify-center">
              <Button 
                className="bg-[#00F0FF] text-black gap-1"
                onClick={() => setShowImportModal(true)}
              >
                <Plus className="w-4 h-4" /> Import a Job
              </Button>
              <Button 
                variant="outline" className={`gap-1 ${isDark ? 'border-white/20' : 'border-gray-300'}`}
                onClick={() => setShowEmailModal(true)}
              >
                <Mail className="w-4 h-4" /> Import from Email
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {jobs.map(job => (
              <Card key={job.id} className={`overflow-hidden ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`} data-testid={`upwork-job-${job.id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="font-bold text-base truncate">{job.title}</h3>
                        <span className={`px-2 py-0.5 text-xs ${STATUS_COLORS[job.status] || STATUS_COLORS.new}`}>
                          {STATUS_LABELS[job.status] || job.status}
                        </span>
                        {job.client_country && (
                          <span className={`px-2 py-0.5 text-xs flex items-center gap-1 ${isDark ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                            <Globe className="w-3 h-3" /> {job.client_country}
                          </span>
                        )}
                      </div>
                      <div className={`flex items-center gap-4 text-xs mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {job.budget && <span className="text-[#00FF94] font-bold">{job.budget}</span>}
                        {job.experience_level && <span>{job.experience_level}</span>}
                        {job.duration && <span>{job.duration}</span>}
                        {job.posted && <span>{job.posted}</span>}
                      </div>
                      <p className={`text-sm line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{job.description?.substring(0, 200)}...</p>
                      {job.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {job.skills.slice(0, 8).map((s, i) => (
                            <span key={i} className={`text-xs px-2 py-0.5 ${isDark ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{s}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      {!job.proposal ? (
                        <Button
                          size="sm"
                          className="bg-[#7000FF] text-white text-xs gap-1"
                          onClick={() => handleGenerateProposal(job.id)}
                          disabled={generatingId === job.id}
                          data-testid={`gen-proposal-${job.id}`}
                        >
                          {generatingId === job.id ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Sparkles className="w-3.5 h-3.5" />
                          )}
                          {generatingId === job.id ? 'Generating...' : 'AI Proposal'}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-[#00F0FF]/30 text-[#00F0FF] text-xs gap-1"
                          onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                          data-testid={`view-proposal-${job.id}`}
                        >
                          {expandedJob === job.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          View Proposal
                        </Button>
                      )}
                      <select
                        value={job.status}
                        onChange={(e) => handleUpdateStatus(job.id, e.target.value)}
                        className={`border px-2 py-1 text-xs ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                      >
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                      <div className="flex gap-1">
                        {job.url && (
                          <a href={job.url} target="_blank" rel="noopener noreferrer" className={`p-1.5 transition-colors ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'}`}>
                            <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                          </a>
                        )}
                        <button onClick={() => handleDeleteJob(job.id)} className={`p-1.5 transition-colors ${isDark ? 'bg-white/5 hover:bg-red-500/20' : 'bg-gray-100 hover:bg-red-50'}`}>
                          <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Proposal */}
                <AnimatePresence>
                  {expandedJob === job.id && job.proposal && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className={`border-t ${isDark ? 'border-white/10 bg-[#0A0A14]' : 'border-gray-200 bg-gray-50'}`}
                    >
                      <div className="p-5">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-bold text-[#00F0FF] uppercase tracking-wider flex items-center gap-2">
                            <Sparkles className="w-4 h-4" /> AI-Generated Proposal
                          </h4>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className={`text-xs gap-1 ${isDark ? 'border-white/20' : 'border-gray-300'}`}
                              onClick={() => handleGenerateProposal(job.id)}
                              disabled={generatingId === job.id}
                            >
                              <RefreshCw className={`w-3 h-3 ${generatingId === job.id ? 'animate-spin' : ''}`} /> Regenerate
                            </Button>
                            <Button
                              size="sm"
                              className={`text-xs gap-1 ${copiedId === job.id ? 'bg-[#00FF94] text-black' : 'bg-[#00F0FF] text-black'}`}
                              onClick={() => handleCopyProposal(job.proposal, job.id)}
                              data-testid={`copy-proposal-${job.id}`}
                            >
                              {copiedId === job.id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                              {copiedId === job.id ? 'Copied!' : 'Copy'}
                            </Button>
                          </div>
                        </div>
                        <div className={`border p-4 max-h-80 overflow-y-auto ${isDark ? 'bg-[#0F0F18] border-white/5' : 'bg-white border-gray-200'}`}>
                          <pre className={`text-sm whitespace-pre-wrap font-sans leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{job.proposal}</pre>
                        </div>
                        {job.proposal_generated_at && (
                          <p className={`text-xs mt-2 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Generated: {new Date(job.proposal_generated_at).toLocaleString()}</p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Import Job Modal */}
      <AnimatePresence>
        {showImportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6"
            onClick={(e) => e.target === e.currentTarget && setShowImportModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`border w-full max-w-lg max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#0F0F18] border-white/10' : 'bg-white border-gray-200'}`}
            >
              <div className={`p-6 border-b flex items-center justify-between ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <h2 className="font-heading text-lg font-bold">Import Upwork Job</h2>
                <button onClick={() => setShowImportModal(false)}>
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className={`text-xs uppercase mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Job Title *</label>
                  <input
                    className={`w-full border px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                    value={importForm.title}
                    onChange={e => setImportForm(prev => ({...prev, title: e.target.value}))}
                    placeholder="e.g., Senior Python Developer for SaaS Platform"
                    data-testid="import-title"
                  />
                </div>
                <div>
                  <label className={`text-xs uppercase mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Upwork URL</label>
                  <input
                    className={`w-full border px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                    value={importForm.url}
                    onChange={e => setImportForm(prev => ({...prev, url: e.target.value}))}
                    placeholder="https://www.upwork.com/jobs/..."
                  />
                </div>
                <div>
                  <label className={`text-xs uppercase mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Job Description *</label>
                  <textarea
                    className={`w-full border px-3 py-2 text-sm h-32 focus:border-[#00F0FF] focus:outline-none resize-none ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                    value={importForm.description}
                    onChange={e => setImportForm(prev => ({...prev, description: e.target.value}))}
                    placeholder="Paste the full job description here..."
                    data-testid="import-description"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`text-xs uppercase mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Budget</label>
                    <input
                      className={`w-full border px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                      value={importForm.budget}
                      onChange={e => setImportForm(prev => ({...prev, budget: e.target.value}))}
                      placeholder="$5,000 - $10,000"
                    />
                  </div>
                  <div>
                    <label className={`text-xs uppercase mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Duration</label>
                    <input
                      className={`w-full border px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                      value={importForm.duration}
                      onChange={e => setImportForm(prev => ({...prev, duration: e.target.value}))}
                      placeholder="1-3 months"
                    />
                  </div>
                </div>
                <div>
                  <label className={`text-xs uppercase mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Skills (comma-separated)</label>
                  <input
                    className={`w-full border px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                    value={importForm.skills}
                    onChange={e => setImportForm(prev => ({...prev, skills: e.target.value}))}
                    placeholder="Python, FastAPI, React, PostgreSQL"
                  />
                </div>
                <div>
                  <label className={`text-xs uppercase mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Experience Level</label>
                  <select
                    className={`w-full border px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                    value={importForm.experience_level}
                    onChange={e => setImportForm(prev => ({...prev, experience_level: e.target.value}))}
                  >
                    <option value="">Select...</option>
                    <option value="Entry Level">Entry Level</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Expert">Expert</option>
                  </select>
                </div>
              </div>
              <div className={`p-6 border-t flex gap-3 justify-end ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <Button variant="outline" className={isDark ? 'border-white/20' : 'border-gray-300'} onClick={() => setShowImportModal(false)}>Cancel</Button>
                <Button className="bg-[#00F0FF] text-black gap-1" onClick={handleImportJob} data-testid="import-submit">
                  <Plus className="w-4 h-4" /> Import Job
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Import Modal */}
      <AnimatePresence>
        {showBulkModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6"
            onClick={(e) => e.target === e.currentTarget && setShowBulkModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`border w-full max-w-2xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#0F0F18] border-white/10' : 'bg-white border-gray-200'}`}
            >
              <div className={`p-6 border-b flex items-center justify-between ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <h2 className="font-heading text-lg font-bold">Bulk Import from Upwork</h2>
                <button onClick={() => setShowBulkModal(false)}>
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Copy the Upwork search results page content and paste below. The system will automatically parse job titles, descriptions, budgets, and skills.
                </p>
                <div>
                  <label className={`text-xs uppercase mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Search Query Label</label>
                  <input
                    className={`w-full border px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                    value={bulkQuery}
                    onChange={e => setBulkQuery(e.target.value)}
                    placeholder="e.g., python developer"
                  />
                </div>
                <div>
                  <label className={`text-xs uppercase mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Pasted Content *</label>
                  <textarea
                    className={`w-full border px-3 py-2 text-sm h-64 focus:border-[#00F0FF] focus:outline-none resize-none font-mono ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                    value={bulkMarkdown}
                    onChange={e => setBulkMarkdown(e.target.value)}
                    placeholder="Paste Upwork search results page content here..."
                    data-testid="bulk-markdown"
                  />
                </div>
              </div>
              <div className={`p-6 border-t flex gap-3 justify-end ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <Button variant="outline" className={isDark ? 'border-white/20' : 'border-gray-300'} onClick={() => setShowBulkModal(false)}>Cancel</Button>
                <Button className="bg-[#00F0FF] text-black gap-1" onClick={handleBulkImport} data-testid="bulk-import-submit">
                  <FileText className="w-4 h-4" /> Import Jobs
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Email Import Modal */}
      <AnimatePresence>
        {showEmailModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6"
            onClick={() => setShowEmailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className={`border w-full max-w-2xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#0F0F18] border-white/10' : 'bg-white border-gray-200'}`}
            >
              <div className={`p-6 border-b flex items-center justify-between ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <div>
                  <h2 className="font-heading text-lg font-bold flex items-center gap-2">
                    <Mail className="w-5 h-5 text-[#00F0FF]" /> Import from Upwork Email
                  </h2>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Paste the Upwork notification email HTML/content. US-only jobs are auto-rejected.
                  </p>
                </div>
                <button onClick={() => setShowEmailModal(false)}>
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className={`p-3 border rounded text-xs ${isDark ? 'bg-[#00F0FF]/5 border-[#00F0FF]/20 text-[#00F0FF]' : 'bg-blue-50 border-blue-200 text-blue-600'}`}>
                  <strong>How to use:</strong> Open the Upwork notification email in your browser, right-click → "View page source" or select all text and paste. The parser will extract job title, description, budget, skills, and client country.
                </div>
                <div>
                  <label className={`text-xs uppercase mb-1 block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Email Content (HTML or plain text) *</label>
                  <textarea
                    className={`w-full border px-3 py-2 text-sm h-64 focus:border-[#00F0FF] focus:outline-none resize-none font-mono ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}
                    value={emailHtml}
                    onChange={e => setEmailHtml(e.target.value)}
                    placeholder="Paste the full email HTML source or text content here..."
                    data-testid="email-html-input"
                  />
                </div>
              </div>
              <div className={`p-6 border-t flex gap-3 justify-end ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <Button variant="outline" className={isDark ? 'border-white/20' : 'border-gray-300'} onClick={() => setShowEmailModal(false)}>Cancel</Button>
                <Button className="bg-[#00F0FF] text-black gap-1" onClick={handleEmailImport} data-testid="email-import-submit">
                  <Mail className="w-4 h-4" /> Import from Email
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
